import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_URL || "https://interventi.synology.me:5050";

const emptyRow = {
  data: "",
  ora_inizio: "",
  ora_fine: "",
  pausa_minuti: 0,
  tecnico: "",
  note: "",
};

const TIME_OPTIONS = Array.from({ length: 96 }, (_, index) => {
  const minuti = index * 15;
  const hh = String(Math.floor(minuti / 60)).padStart(2, "0");
  const mm = String(minuti % 60).padStart(2, "0");
  return `${hh}:${mm}`;
});

const formatMinuti = (minuti) => {
  const m = Number(minuti);
  if (!Number.isFinite(m) || m < 0) return "-";
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const toTimeInput = (value) => {
  if (!value) return "";
  return String(value).split(":").slice(0, 2).join(":");
};

const toDateInput = (value) => {
  if (!value) return "";

  const s = String(value);

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return s.slice(0, 10);
  }

  return "";
};

const normalizeRow = (row) => ({
  ...row,
  data: toDateInput(row.data),
  ora_inizio: toTimeInput(row.ora_inizio),
  ora_fine: toTimeInput(row.ora_fine),
  pausa_manuale_minuti: Number(
    row.pausa_manuale_minuti ?? row.pausa_minuti
  ) || 0,
  pausa_auto_minuti: Number(row.pausa_auto_minuti) || 0,
  pausa_minuti: Number(row.pausa_minuti) || 0,
});


const TimeSelect = ({ value, onChange }) => {
  const valore = toTimeInput(value);
  const valoreFuoriLista =
    valore && !TIME_OPTIONS.includes(valore);

  return (
    <select
      value={valore}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">--:--</option>

      {valoreFuoriLista && (
        <option value={valore}>{valore}</option>
      )}

      {TIME_OPTIONS.map((ora) => (
        <option key={ora} value={ora}>
          {ora}
        </option>
      ))}
    </select>
  );
};

const GiornateIntervento = ({
  interventoId,
  tecnicoDefault = "Carrino",
}) => {
  // Carrino minimo V10
  const [giornate, setGiornate] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [dirtyIds, setDirtyIds] = useState([]);

  const [newRow, setNewRow] = useState({
    ...emptyRow,
    tecnico: tecnicoDefault || "Carrino",
  });


  useEffect(() => {
    const fallback = String(tecnicoDefault || "").trim() || "Carrino";

    setNewRow((prev) => {
      if (String(prev.tecnico || "").trim()) {
        return prev;
      }

      return {
        ...prev,
        tecnico: fallback,
      };
    });
  }, [tecnicoDefault]);

  const canUse = Boolean(interventoId);

  const loadGiornate = async () => {
    if (!interventoId) return;

    setLoading(true);

    try {
      const res = await axios.get(
        `${API_BASE_URL}/interventi/${interventoId}/giornate`
      );

      setGiornate(
        Array.isArray(res.data)
          ? res.data.map(normalizeRow)
          : []
      );

      setDirtyIds([]);
    } catch (err) {
      console.error("Errore caricamento giornate:", err);
      alert("Errore caricamento giornate intervento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGiornate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interventoId]);

  useEffect(() => {
    const onRefresh = (event) => {
      const eventId = Number(event?.detail?.interventoId);
      if (!eventId || eventId === Number(interventoId)) {
        loadGiornate();
      }
    };

    window.addEventListener("betalumina:giornate-refresh", onRefresh);
    return () => {
      window.removeEventListener("betalumina:giornate-refresh", onRefresh);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interventoId]);

  const totals = useMemo(() => {
    return giornate.reduce(
      (acc, g) => {
        acc.pausa += Number(g.pausa_minuti) || 0;
        acc.effettive += Number(g.durata_minuti) || 0;
        acc.conteggiate +=
          Number(g.durata_conteggiata_minuti) || 0;

        return acc;
      },
      {
        pausa: 0,
        effettive: 0,
        conteggiate: 0,
      }
    );
  }, [giornate]);

  const giornateRaggruppate = useMemo(() => {
    const gruppi = new Map();

    [...giornate]
      .sort((a, b) => {
        const dataA = a.data || "9999-99-99";
        const dataB = b.data || "9999-99-99";

        if (dataA !== dataB) {
          return dataA.localeCompare(dataB);
        }

        return Number(a.id || 0) - Number(b.id || 0);
      })
      .forEach((g) => {
        const key = g.data || "senza-data";

        if (!gruppi.has(key)) {
          gruppi.set(key, []);
        }

        gruppi.get(key).push(g);
      });

    return Array.from(gruppi.entries());
  }, [giornate]);

  const marcaModificata = (id) => {
    setDirtyIds((prev) =>
      prev.includes(id) ? prev : [...prev, id]
    );
  };

  const updateLocalRow = (id, field, value) => {
    setGiornate((prev) =>
      prev.map((g) =>
        g.id === id
          ? {
              ...g,
              [field]:
                field === "pausa_manuale_minuti"
                  ? Math.max(
                      0,
                      parseInt(value || "0", 10) || 0
                    )
                  : value,
            }
          : g
      )
    );

    marcaModificata(id);
  };

  const saveRow = async (row) => {
    if (!row.data) {
      alert("La giornata non ha una data valida.");
      return;
    }

    if (!String(row.tecnico || "").trim()) {
      alert("Inserisci il tecnico.");
      return;
    }

    if (!row.ora_inizio || !row.ora_fine) {
      alert("Seleziona ora di inizio e ora di fine.");
      return;
    }

    setSavingId(row.id);

    try {
      const payload = {
        data: row.data,
        ora_inizio: row.ora_inizio,
        ora_fine: row.ora_fine,
        pausa_manuale_minuti: Math.max(
          0,
          parseInt(row.pausa_manuale_minuti || 0, 10) || 0
        ),
        pausa_minuti: Math.max(
          0,
          parseInt(row.pausa_manuale_minuti || 0, 10) || 0
        ),
        tecnico: String(row.tecnico).trim(),
        note: row.note || null,
      };

      const res = await axios.put(
        `${API_BASE_URL}/interventi-giornate/${row.id}`,
        payload
      );

      const updated = normalizeRow(res.data);

      setGiornate((prev) =>
        prev.map((g) =>
          g.id === row.id ? updated : g
        )
      );

      setDirtyIds((prev) =>
        prev.filter((id) => id !== row.id)
      );
    } catch (err) {
      console.error("Errore salvataggio tecnico:", err);
      alert("Errore salvataggio tecnico");
    } finally {
      setSavingId(null);
    }
  };

  const preparaNuovaGiornata = () => {
    setNewRow({
      ...emptyRow,
      data: "",
      tecnico: tecnicoDefault || "Carrino",
    });

    setTimeout(() => {
      document
        .getElementById("nuovo-tecnico-giornata")
        ?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
    }, 0);
  };

  const addRow = async () => {
    if (!interventoId) {
      alert("Salva prima l'intervento.");
      return;
    }

    if (!newRow.data) {
      alert("Seleziona la data della giornata.");
      return;
    }

    if (!String(newRow.tecnico || "").trim()) {
      alert("Inserisci il tecnico.");
      return;
    }

    if (!newRow.ora_inizio || !newRow.ora_fine) {
      alert("Seleziona ora di inizio e ora di fine.");
      return;
    }

    try {
      const payload = {
        data: newRow.data,
        ora_inizio: newRow.ora_inizio,
        ora_fine: newRow.ora_fine,
        pausa_manuale_minuti: Math.max(
          0,
          parseInt(newRow.pausa_minuti || 0, 10) || 0
        ),
        pausa_minuti: Math.max(
          0,
          parseInt(newRow.pausa_minuti || 0, 10) || 0
        ),
        tecnico: String(newRow.tecnico).trim(),
        note: newRow.note || null,
      };

      const res = await axios.post(
        `${API_BASE_URL}/interventi/${interventoId}/giornate`,
        payload
      );

      setGiornate((prev) => [
        ...prev,
        normalizeRow(res.data),
      ]);

      setNewRow({
        ...emptyRow,
        data: "",
        tecnico: tecnicoDefault || "Carrino",
      });
    } catch (err) {
      console.error("Errore creazione giornata:", err);
      alert("Errore creazione giornata");
    }
  };

  const deleteRow = async (id) => {
    if (
      !window.confirm(
        "Eliminare questa prestazione del tecnico?"
      )
    ) {
      return;
    }

    try {
      await axios.delete(
        `${API_BASE_URL}/interventi-giornate/${id}`
      );

      setGiornate((prev) =>
        prev.filter((g) => g.id !== id)
      );

      setDirtyIds((prev) =>
        prev.filter((dirtyId) => dirtyId !== id)
      );
    } catch (err) {
      console.error("Errore eliminazione tecnico:", err);
      alert("Errore eliminazione tecnico");
    }
  };

  return (
    <div className="giornate-intervento">
      <div className="giornate-header">
        <div>
          <h3>Giornate intervento</h3>
          <p>
            Una riga per ogni tecnico e giornata di lavoro
          </p>
        </div>

        {canUse && (
          <button
            type="button"
            className="btn"
            onClick={preparaNuovaGiornata}
          >
            + Nuova riga
          </button>
        )}
      </div>

      {!canUse && (
        <div className="giornate-avviso">
          Salva prima l’intervento. Dopo il primo salvataggio
          potrai aggiungere giornate e tecnici.
        </div>
      )}

      {canUse && (
        <>
          {loading ? (
            <p>Caricamento giornate...</p>
          ) : (
            <>
              <div className="giornate-table-wrap">
                <table className="giornate-table giornate-table--unica">
                  <colgroup>
                    <col className="giornate-col-data" />
                    <col className="giornate-col-tecnico" />
                    <col className="giornate-col-ora" />
                    <col className="giornate-col-ora" />
                    <col className="giornate-col-pausa" />
                    <col className="giornate-col-effettive" />
                    <col className="giornate-col-conteggiate" />
                    <col className="giornate-col-note" />
                    <col className="giornate-col-azioni" />
                  </colgroup>

                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Tecnico</th>
                      <th>Inizio</th>
                      <th>Fine</th>
                      <th>Pausa min</th>
                      <th>Effettive</th>
                      <th>Conteggiate</th>
                      <th>Note</th>
                      <th>Azioni</th>
                    </tr>
                  </thead>

                  <tbody>
                    {giornateRaggruppate
                      .flatMap(([, righe]) => righe)
                      .map((g) => {
                        const dirty = dirtyIds.includes(g.id);

                        return (
                          <tr
                            key={g.id}
                            className={
                              dirty
                                ? "giornate-row-dirty"
                                : ""
                            }
                          >
                            <td>
                              <input
                                type="date"
                                value={g.data || ""}
                                onChange={(e) =>
                                  updateLocalRow(
                                    g.id,
                                    "data",
                                    e.target.value
                                  )
                                }
                              />
                            </td>

                            <td>
                              <input
                                type="text"
                                value={g.tecnico || ""}
                                onChange={(e) =>
                                  updateLocalRow(
                                    g.id,
                                    "tecnico",
                                    e.target.value
                                  )
                                }
                                placeholder="Tecnico"
                              />
                            </td>

                            <td>
                              <TimeSelect
                                value={g.ora_inizio}
                                onChange={(value) =>
                                  updateLocalRow(
                                    g.id,
                                    "ora_inizio",
                                    value
                                  )
                                }
                              />
                            </td>

                            <td>
                              <TimeSelect
                                value={g.ora_fine}
                                onChange={(value) =>
                                  updateLocalRow(
                                    g.id,
                                    "ora_fine",
                                    value
                                  )
                                }
                              />
                            </td>

                            <td>
                              <input
                                type="number"
                                min="0"
                                step="1"
                                value={g.pausa_manuale_minuti ?? 0}
                                onChange={(e) =>
                                  updateLocalRow(
                                    g.id,
                                    "pausa_manuale_minuti",
                                    e.target.value
                                  )
                                }
                              />
                                                            {Number(g.pausa_auto_minuti) > 0 && (
                                  <div
                                    style={{
                                      marginTop: 4,
                                      fontSize: 11,
                                      color: "#607b91",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    + {formatMinuti(g.pausa_auto_minuti)} auto
                                  </div>
                                )}
</td>

                            <td className="giornate-valore">
                              {formatMinuti(
                                g.durata_minuti
                              )}
                            </td>

                            <td className="giornate-valore">
                              {formatMinuti(
                                g.durata_conteggiata_minuti
                              )}
                            </td>

                            <td>
                              <input
                                type="text"
                                value={g.note || ""}
                                onChange={(e) =>
                                  updateLocalRow(
                                    g.id,
                                    "note",
                                    e.target.value
                                  )
                                }
                                placeholder="Note"
                              />
                            </td>

                            <td>
                              <div className="giornate-azioni">
                                <button
                                  type="button"
                                  className="btn giornate-btn-save"
                                  onClick={() => saveRow(g)}
                                  disabled={savingId === g.id}
                                >
                                  {savingId === g.id
                                    ? "..."
                                    : dirty
                                    ? "Salva *"
                                    : "Salva"}
                                </button>

                                <button
                                  type="button"
                                  className="delete-btn giornate-btn-delete"
                                  onClick={() =>
                                    deleteRow(g.id)
                                  }
                                >
                                  Elimina
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                    <tr
                      id="nuovo-tecnico-giornata"
                      className="giornate-row-new"
                    >
                      <td>
                        <input
                          type="date"
                          value={newRow.data}
                          onChange={(e) =>
                            setNewRow((prev) => ({
                              ...prev,
                              data: e.target.value,
                            }))
                          }
                        />
                      </td>

                      <td>
                        <input
                          type="text"
                          value={newRow.tecnico}
                          onChange={(e) =>
                            setNewRow((prev) => ({
                              ...prev,
                              tecnico: e.target.value,
                            }))
                          }
                          placeholder="Tecnico"
                        />
                      </td>

                      <td>
                        <TimeSelect
                          value={newRow.ora_inizio}
                          onChange={(value) =>
                            setNewRow((prev) => ({
                              ...prev,
                              ora_inizio: value,
                            }))
                          }
                        />
                      </td>

                      <td>
                        <TimeSelect
                          value={newRow.ora_fine}
                          onChange={(value) =>
                            setNewRow((prev) => ({
                              ...prev,
                              ora_fine: value,
                            }))
                          }
                        />
                      </td>

                      <td>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={newRow.pausa_minuti}
                          onChange={(e) =>
                            setNewRow((prev) => ({
                              ...prev,
                              pausa_minuti: Math.max(
                                0,
                                parseInt(
                                  e.target.value || "0",
                                  10
                                ) || 0
                              ),
                            }))
                          }
                        />
                      </td>

                      <td className="giornate-valore">—</td>
                      <td className="giornate-valore">—</td>

                      <td>
                        <input
                          type="text"
                          value={newRow.note}
                          onChange={(e) =>
                            setNewRow((prev) => ({
                              ...prev,
                              note: e.target.value,
                            }))
                          }
                          placeholder="Note"
                        />
                      </td>

                      <td>
                        <button
                          type="button"
                          className="btn giornate-btn-add"
                          onClick={addRow}
                        >
                          + Aggiungi
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {giornate.length === 0 && (
                <div className="giornate-vuoto">
                  Nessuna prestazione registrata. Compila la
                  prima riga della tabella.
                </div>
              )}

              {giornate.length > 0 && (
                <div className="giornate-totale-intervento">
                  <span>
                    <strong>Pause/uomo totali:</strong>{" "}
                    {formatMinuti(totals.pausa)}
                  </span>

                  <span>
                    <strong>Ore/uomo effettive:</strong>{" "}
                    {formatMinuti(totals.effettive)}
                  </span>

                  <span>
                    <strong>Ore/uomo conteggiate:</strong>{" "}
                    {formatMinuti(totals.conteggiate)}
                  </span>
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
};

export default GiornateIntervento;
